import { Skeleton } from "@attmos/flowstack-ui";

import { useArtifactDetail } from "@/api/artifacts";
import { EmptyState } from "@/components/layout/EmptyState";
import { AxisPicker } from "@/components/runs/AxisPicker";
import type { MoleculeSubject } from "@/components/runs/molecule-subjects";
import { subjectKeyToFragmentId } from "@/components/runs/molecule-subjects";
import type { RunVisualView } from "@/components/runs/run-visual-views";
import { MoleculeViewerPanel } from "@/components/viz/MoleculeViewerPanel";

/**
 * The two renderable halves of a run's VISUAL, split out of the old
 * `RunVisualPanel` (flowstack#950 Part B, #956) when #991 turned the
 * workspace into a mode switch.
 *
 * What changed and why: the panel used to own three things at once — the
 * view SELECTOR, the empty states, and the molecule. #991 promotes the
 * selector to the workspace's main-area mode switch (a run's visual is no
 * longer a thing tucked in a 26rem column; it can BE the main area), and both
 * the main area and the side panel now need to draw the same molecule. A
 * component that owned its own selection could not serve both, so what is
 * left here is the drawing, and the workspace owns which of it to draw.
 *
 * The properties #950/#956 established are unchanged, and this file is still
 * where two of them live:
 *
 * - **A view whose data does not exist yet is PENDING, never empty-and-silent
 *   and never a failure** — three distinguishable states, and a fourth
 *   ("produced, failed to load") that each view raises from its own fetch.
 *   Collapsing any of them into another is flowstack#923.
 * - **A step pinned to nothing renderable is its OWN state**, not the run's:
 *   falling back to the run subject would claim the step operated on the
 *   ligand when it may have consumed a force field or a topology.
 */

/**
 * The empty state for a view that is not `ready`.
 *
 * Deliberately a separate export rather than a branch inside the molecule:
 * the workspace shows this in the MAIN area when the picked mode's data has
 * not arrived, which is a different place from where the structure renders.
 */
export function RunVisualUnavailable({
  view,
  runIsLive,
}: {
  view: RunVisualView;
  runIsLive: boolean;
}): JSX.Element {
  const { title, description } = describeUnavailable(view, runIsLive);
  return <EmptyState title={title} description={description} />;
}

/**
 * Which sentence a not-ready view gets, and why it is a function rather than
 * nested ternaries (Rupert PR-999 r1).
 *
 * The states are not a two-way branch: *pinned-empty*, *pending while live*,
 * *pending but merely unread*, and *absent* are four different claims with
 * four different remedies, and the file's own header notes a fifth
 * ("produced, failed to load") that each view raises from its own fetch. Read
 * as a ternary chain that is easy to mis-nest on the next addition, and
 * mis-nesting it produces exactly the flowstack#923 conflation — a wrong but
 * plausible sentence about someone's run.
 *
 * Exported for its own test, so each branch is pinned by the claim it makes
 * rather than only through a rendered component.
 */
export function describeUnavailable(
  view: RunVisualView,
  runIsLive: boolean
): { title: string; description: string } {
  const noun = view.label.toLowerCase();
  // A step pinned to nothing renderable is its OWN state: falling back to the
  // run subject would claim the step operated on the ligand when it may have
  // consumed a force field or a topology.
  if (view.id === "molecule" && view.pinnedToNode) {
    return {
      title: `${view.pinnedToNode} has no molecule input.`,
      description:
        "This step consumes something with no structure to draw — a force field, a topology or a log. Clear the selection to see the run's molecule.",
    };
  }
  if (view.availability === "pending") {
    return {
      title: `No ${noun} yet.`,
      description: runIsLive
        ? "Views appear here as the flow's steps publish their outputs."
        : "Still reading this run's outputs.",
    };
  }
  return {
    title: `This run produced no ${noun}.`,
    description:
      "Not every step has something to draw — a force-field bundle, a topology or a log has no visual.",
  };
}

/**
 * What the molecule section CALLS what it is drawing, and the sentence beneath
 * it when that needs one.
 *
 * Three states, and they are three claims rather than a default plus two
 * decorations:
 *
 * - **`Input to <node>`** — the pin (flowstack#956). This step consumed this
 *   structure; the panel is the answer to "what did it operate on".
 * - **`Run molecule`** — the rail's fallback (flowstack#1237). The selected
 *   step consumed no structure, so what is on screen is the run's subject and
 *   the heading says so. The heading carries the CLAIM (whose molecule this is)
 *   and stays short enough to survive the rail's truncation; the note carries
 *   the reason, because a fallback the reader cannot see is a substitution
 *   made silently, which is the thing `deriveRunVisualViews` refuses.
 * - **`Molecule`** — nothing selected. The run's subject, with no step to
 *   contrast it against.
 *
 * A function, and exported, for the reason `describeUnavailable` is one: these
 * are sentences about someone's run, and each should be pinnable by the claim
 * it makes rather than only through a mounted component. Shared by the rail and
 * the main pane so the two cannot drift — the main pane's view never carries
 * `subjectFallbackFromNode`, so it simply never reaches the middle branch.
 */
export function describeMolecule(view: RunVisualView): { heading: string; note: string | null } {
  // `?? null`, not `=== null`: `fragmentId` is OPTIONAL on the view (an API
  // build predating #1320, and every non-molecule view), so `undefined` is the
  // common absent value and a strict null check would print the word
  // "undefined" into a sentence about someone's run.
  const fragmentId = view.fragmentId ?? null;
  if (view.pinnedToNode) {
    return {
      heading: `Input to ${view.pinnedToNode}`,
      // WHICH fragment, when the index can say (flowstack#1320). The pin is
      // the one molecule state with no picker above it — the subject there is
      // the step's, not the run's — so the naming the operator asked for has
      // to be said in words here or not at all. In the NOTE rather than the
      // heading because the heading is truncated to the rail's column and an
      // elided `Input to torsion_fit: fr…` loses exactly the half that is new.
      note: fragmentId === null ? null : `This step's input is fragment ${fragmentId}.`,
    };
  }
  if (view.subjectFallbackFromNode) {
    return {
      heading: "Run molecule",
      note:
        `${view.subjectFallbackFromNode} consumed no structure to draw, ` +
        "so this is the molecule the run is about — not that step's input.",
    };
  }
  return { heading: "Molecule", note: null };
}

/**
 * What the molecule view needs in order to be POINTED at a structure
 * (flowstack#1320) — the run's selectable set, which of them is on screen, and
 * where a change goes.
 *
 * The list is `moleculeSubjects`' — resolved once by the workspace and passed
 * down, rather than re-derived here, so the entry the picker offers and the
 * artifact the viewer draws cannot be two different answers to the same
 * question.
 */
export interface MoleculeSubjectPick {
  subjects: readonly MoleculeSubject[];
  /** The fragment ON SCREEN (`RunVisualView.fragmentId`), `null` for the
   * ligand — not the raw pick, which may not have resolved. */
  fragmentId: string | null;
  onSelect: (fragmentId: string | null) => void;
}

/**
 * The structure picker: WHICH of the run's molecules the viewer is on, always
 * saying which even when there is nothing to switch to.
 *
 * **`AxisPicker`, not a control of its own** (VIZ U5.4). This is the same
 * shape as the fragment and torsion axes it already serves — a single-choice
 * set where exactly one member is on screen — so it inherits the radiogroup
 * semantics, the roving tab stop, and the pills-to-`<select>` fallback above
 * `MAX_PILLS` rather than growing a second set of each. That fallback is the
 * reason nothing here is written against a fragment count: two fragments, the
 * three `jnk1_18658` cuts, and a ligand that cuts twenty all render from the
 * same list.
 *
 * **It renders at n=1 too, and that is the point.** `AxisPicker` draws its
 * caption — the axis name, the current value, `1 of 1` — for a single-member
 * axis as well, so a run with no fragments still says *ligand* rather than
 * showing an unlabelled molecule. Ambiguity about what is on screen is the
 * complaint underneath #1320 and it exists at every count.
 */
function MoleculeSubjectPicker({
  subjects,
  fragmentId,
  onSelect,
}: MoleculeSubjectPick): JSX.Element | null {
  const current = subjects.find((s) => s.fragmentId === fragmentId) ?? null;
  return (
    <AxisPicker
      axisLabel="Structure"
      values={subjects.map((s) => s.key)}
      // `AxisPicker` renders nothing for a value outside its own axis, which is
      // the honest outcome here: the viewer is on a structure the run's subject
      // list does not contain, so there is no member to mark.
      value={current?.key ?? null}
      onChange={(key) => onSelect(subjectKeyToFragmentId(subjects, key))}
    />
  );
}

/**
 * The molecule itself.
 *
 * Fetches the artifact detail by CONTENT HASH and hands it to the shared
 * `MoleculeViewerPanel`, which owns the lazy boundary around 3Dmol.js and the
 * type→payload selection. Same route Part A's submission preview takes, for
 * the same reason: the only thing this component can render is the artifact
 * the catalog holds, so there is no code path by which it could show
 * something other than what the run will actually compute on.
 */
export function RunSubjectMolecule({
  contentHash,
  fill,
  siblingCount,
  heading = "Molecule",
  note = null,
  subject = null,
}: {
  contentHash: string | null;
  fill: boolean;
  siblingCount: number;
  /** Says WHOSE structure this is; the workspace passes `describeMolecule`. */
  heading?: string;
  /** The sentence under the viewer when the heading alone cannot carry the
   * whole claim — the rail's fallback says which step it fell back from. */
  note?: string | null;
  /**
   * WHICH of the run's structures to draw, as a picker (flowstack#1320).
   *
   * `null` where the subject is not the run's to choose — the step pin, whose
   * molecule is that step's input and whose naming is {@link describeMolecule}'s
   * note instead.
   */
  subject?: MoleculeSubjectPick | null;
}): JSX.Element {
  const { data, isLoading, error } = useArtifactDetail(contentHash);

  // ABOVE every branch, including the loading and error ones. The label is the
  // answer to "which molecule am I looking at", and a surface that can only
  // answer it once the payload has arrived is ambiguous for exactly the window
  // in which the user is waiting to find out.
  const picker = subject === null ? null : <MoleculeSubjectPicker {...subject} />;

  if (isLoading) {
    return (
      <div className={fill ? "flex min-h-0 flex-1 flex-col gap-2" : "space-y-2"}>
        {picker}
        <Skeleton className={fill ? "h-full min-h-0 w-full flex-1" : "h-[320px] w-full"} />
      </div>
    );
  }
  if (error) {
    return (
      <div className="space-y-2">
        {picker}
        <EmptyState
          title="Could not load the molecule."
          description={error instanceof Error ? error.message : "Unknown error."}
        />
      </div>
    );
  }
  if (!data) {
    return (
      <div className="space-y-2">
        {picker}
        <EmptyState
          title="Could not load the molecule."
          description="The catalog returned no record for this artifact."
        />
      </div>
    );
  }
  return (
    <div className={fill ? "flex min-h-0 flex-1 flex-col gap-2" : undefined}>
      {picker}
      <MoleculeViewerPanel artifact={data} heading={heading} fill={fill} />
      {note !== null && (
        <p className="mt-1 text-[11px] text-muted-foreground" data-testid="molecule-note">
          {note}
        </p>
      )}
      {siblingCount > 1 && (
        // A FANNED step consumes a different artifact per child (one
        // fragment each). Showing one without saying so would assert
        // "this is what the step ran on" about a step that ran on N
        // things.
        <p className="mt-1 text-[11px] text-muted-foreground">
          Showing 1 of {siblingCount} inputs this step consumed across its children.
        </p>
      )}
    </div>
  );
}
